import Vue from 'vue';
import Vuex from 'vuex';
import axios from 'axios';
import { EventBus } from '../../lib/event-bus.js';

// initial state
const state = {
	quiz: [],
	// Quiz settings:
	quizSettings: [],
	given_answers: [],
	questions: [],
	response_id: null,
	last_answer_given: 0,
	current_question_index: 0,
	question_media: [],
	score: 0,
	quiz_started: false,
	quiz_start_time: 0,
	timer_on: 0,
	timer: 0,
	answer_confirmed: {
		is_confirmed: false,
		is_correct: false,
		display: true,
		correct_answer: '',
	},
	new_answer: {},
	display_quiz_ended: false,
	quiz_finished: false,
	timer_passed: false,
	new_timer: false,
	stop_timer: false,
	passed_quiz: false,
	percentage: 0,
	max: 0,
	current_answer: null,
};

// getters
const getters = {
	//retrieve the progress of an question by progress id
	getQuestionProgress: (state) => (id) => {
		if (state.given_answers.length > 0) {
			return state.given_answers.find(progress => progress.question_id === id);
		}

		return undefined;
	},

	//retrieve an media item by id
	getQuestionMedia: (state) => (id) => {
		if (state.given_answers.length > 0) {
			return state.question_media.find(media => media.question_id === id);
		}

		return false;
	},

	//retrieve the amount of questions
	getAmountOfQuestions: (state) => {
		return state.questions.length;
	},

	getAmountOfAnsweredQuestions: (state) => {
		return state.given_answers.length;
	},

	//retrieve a question by id
	getQuestionIndexById: (state) => (id) => {
		if (state.questions.length > 0) {
			return state.questions.findIndex(question => question.id === id);
		}

		return false;
	},

	//retrieve an answer option for a multiple choice questions by id
	getQuestionOptionById: (state) => (id) => {
		return state.questions[state.current_question_index].question_options.find(option => option.id === id);
	},

	//get the amount of correct answers given
	getAmountOfCorrectAnswers: (state) => {
		return state.given_answers.filter(question => question.is_correct).length;
	}
};

// mutations
const mutations = {
	setQuiz(state, payload) {
		state.quiz = payload.data;
	},
	setQuizSettings(state, payload) {
		state.quizSettings = payload.data;
	},
	setGivenAnswers(state, payload) {
		state.given_answers = payload.data.data.user_responses;
	},
	setQuestions(state, questions) {
		state.questions = _.orderBy(questions, 'index');
	},
	updateResponseId(state, payload) {
		if (!state.response_id) {
			state.response_id = payload;
		}
	},
	navigateQuestion(state, payload) {
		//navigate to previous/next question
		state.current_question_index = state.current_question_index + payload;
		state.answer_confirmed.is_confirmed = false;
		state.answer_confirmed.is_correct = false;
		state.answer_confirmed.correct_answer = '';
		state.answer_confirmed.display = true;
		state.new_answer = {};

		EventBus.$emit('quiz-question-changed', payload === 1);
	},
	navigateToQuestionByIndex(state, payload) {
		//navigate to question by an index - used for continuing a quiz
		state.current_question_index = payload;
	},
	getLastQuestion(state, payload) {
		state.last_answer_given = payload;
	},
	setQuestionMedia(state, payload) {
		for (let i = 0; i < payload.length; i++) {
			state.question_media.push(payload[i]);
		}
	},
	setTotalScore(state, payload) {
		if (!state.quiz_finished) {
			let data = payload.data.data.user_responses;

			_.forEach(data, function (value) {
				if (value.score) {
					state.score += value.score;
				}
			});
		}
	},
	updateProgress(state, payload) {
		//calculate new score
		state.score = 0;

		_.forEach(payload.user_responses, function (value) {
			state.score += value.score;
		});

		//update the progress with the latest data
		state.given_answers = payload.user_responses;
	},
	startQuiz(state, payload) {
		state.quiz_started = payload;
	},
	setNewAnswer(state, payload) {
		if (payload.type === 'true_false' || payload.type === 'free_form') {
			state.new_answer.value = payload.value;
		} else {
			state.new_answer.question_option_id = payload.value;
		}
	},
	confirmAnswer(state, payload) {
		if (state.quizSettings.direct_feedback === '1') {
			state.answer_confirmed.display = true;
			state.answer_confirmed.is_confirmed = true;
			state.answer_confirmed.is_correct = payload.is_correct;

			//if we want to display the correct answer and the answer is not correct
			if (state.quizSettings.correct_answer_feedback === '1' && !payload.is_correct) {
				if (payload.correct_question_option_id) {
					state.answer_confirmed.correct_answer = payload.correct_question_option_id;
				} else {
					state.answer_confirmed.correct_answer = payload.correct_answer;
				}
			}
		}
	},
	removeAnswerConfirmation(state) {
		state.answer_confirmed.is_confirmed = false;
		state.answer_confirmed.is_correct = false;
		state.answer_confirmed.display = true;
		state.answer_confirmed.correct_answer = '';
	},
	dismissAnswerFeedback(state) {
		state.answer_confirmed.display = false;
	},
	endQuiz(state) {
		state.display_quiz_ended = true;
		state.quiz_finished = true;
		state.stop_timer = true;
	},
	setFinalScore(state, score) {
		state.passed_quiz = score.passed;
		state.percentage = score.percentage;
		state.max = score.max;
		state.score = score.score;
	},
	toggleQuizEndDisplay(state, bool) {
		state.display_quiz_ended = bool;
	},
	getQuizStartedTime(state, payload) {
		state.quiz_start_time = payload;
	},
	getTimeLeft(state, future) {
		//log the future time minus the current time in minutes and seconds
		state.timer = moment.utc(moment(future, 'YYYY-MM-DD HH:mm:ss').diff(moment().format('YYYY-MM-DD HH:mm:ss'))).format('HH:mm:ss');
	},
	timerhasPassed(state, payload) {
		//log the future time minus the current time in minutes and seconds
		state.timer_passed = payload;
		state.answer_confirmed.display = false;
	},
	setNewTimer(state, payload) {
		state.new_timer = true;
	},
	resetQuiz(state) {
		state.given_answers = [];
		state.response_id = null;
		state.last_answer_given = 0;
		state.current_question_index = 0;
		state.score = 0;
		state.quiz_started = false;
		state.timer_on = 0;
		state.timer = 0;
		state.answer_confirmed = {
			is_confirmed: false,
			is_correct: false,
			display: true,
			correct_answer: '',
		};
		state.new_answer = {};
		state.display_quiz_ended = false;
		state.quiz_finished = false;
		state.timer_passed = false;
		state.new_timer = true;
		state.stop_timer = false;
		state.passed_quiz = false;
		state.percentage = 0;
		state.max = 0;
		state.current_answer = null;
	},
	setCurrentAnswer(state, answer) {
		state.current_answer = answer;
	},
};

// actions
const actions = {
	getQuiz({ commit }, payload) {
		let id = payload;
		return axios.get('/api/v3/quiz-maker/quizzes/' + id)
			.then(function (response) {
				commit('setQuiz', response.data);
				commit('setQuestions', response.data.data.questions);
			}).catch((response) => {
			});
	},

	// To get the quiz settings, the state quiz must be updated first
	getQuizSettings({ commit }) {
		return axios.get('/api/v3/quiz-maker/quizzes/' + state.quiz.id + '/settings')
			.then(function (response) {
				commit('setQuizSettings', response.data);
			}).catch((response) => {
			});
	},

	getQuizResponseId({ state, commit }, payload) {
		return new Promise((resolve, reject) => {
			axios.get('/api/v3/quiz-maker/quizzes/' + state.quiz.id + '/user/' + user.id + '/last')
				.then((response) => {
					//if the user has done the quiz before
					if (response.data.user_responses && response.data.user_responses.length > 0) {
						//get the last answered question
						let last_user_response = response.data.user_responses[response.data.user_responses.length - 1];

						//update the response id
						commit('updateResponseId', last_user_response.response_id);

						//confirm the selected answer
						commit('confirmAnswer', last_user_response);

						// Set the question to the progress of the user
						commit('getLastQuestion', last_user_response.question_id);

						// Get the created_at date of this quiz of this user
						commit('getQuizStartedTime', response.data.created_at);

						//if the quiz is finished
						if (response.data.is_finished) {
							// save the score
							const score = response.data.score;

							if (score) {
								commit('setFinalScore', score);
							}

							//end the quiz
							commit('endQuiz', response);

							//hide the confirmation box
							commit('removeAnswerConfirmation');
						}
					} else {
						// Set a new time for the timer
						commit('setNewTimer', true);
					}

					resolve(response);
				}, error => {
					reject(error);
				});
		});
	},
	getGivenAnswers({ state, commit }, payload) {
		if (state.response_id) {
			return axios.get('/api/v3/quiz-maker/responses/' + state.response_id)
				.then(function (response) {
					if (response.data.data.user_responses) {
						commit('setGivenAnswers', response);
					}
					commit('setTotalScore', response);
				}).catch((response) => {
				});
		}
	},
	getCurrentProgress({ state, commit }, payload) {
		return new Promise((resolve, reject) => {
			axios.get('/api/v3/quiz-maker/responses/' + state.response_id)
				.then(function (response) {
					commit('updateProgress', response.data.data);
					resolve(response);
				}, error => {
					this.commit('quiz/timerhasPassed', true);
					reject(error);
				});
		});
	},
	getQuestionMedia({ context, commit }, payload) {
		return axios.get('/api/v3/quiz-maker/questions/' + payload + '/media')
			.then(function (response) {
				commit('setQuestionMedia', response.data.data);
			}).catch((response) => {
			});
	},

	postAnswer({ context, commit }, payload) {
		return new Promise((resolve, reject) => {
			let headers = {
				headers: {
					'user_id': payload.user_id,
				}
			};

			axios.post('/api/v3/quiz-maker/responses/', payload, headers)
				.then((response) => {
					this.commit('quiz/confirmAnswer', response.data.data.responses[0]);
					this.commit('quiz/setCurrentAnswer', null);

					resolve(response);
				}, error => {
					this.commit('quiz/timerhasPassed', true);
					reject(error);
				});
		});
	},
	startQuiz({ context, commit }) {
		let data = {
			user_id: user.id,
		};

		return new Promise((resolve, reject) => {
			axios.post('/api/v3/quiz-maker/quizzes/' + state.quiz.id + '/start-quiz', data)
				.then((response) => {
					commit('startQuiz', true);
					this.commit('quiz/updateResponseId', response.data.data.id);
					this.commit('quiz/setCurrentAnswer', null);

					// If the quiz is done for the first time set the timer time to now
					if (state.new_timer === true) {
						let now = moment().format('YYYY-MM-DD HH:mm:ss');
						commit('getQuizStartedTime', now);
					}

					resolve(true);
				}).catch((response) => {
					reject(response);
				});
		});
	},
};

export default {
	namespaced: true,
	state,
	getters,
	actions,
	mutations,
};